import { useState } from "react";
import { currency } from "../../lib/formatting";
import { budgetAmountToMonthly } from "../../lib/calculations";
import type { FundingSource } from "../settings/SettingsPage";
import { NumberInput } from "../ui/NumberInput";

interface AddFundingSourceModalProps {
  onAdd: (source: FundingSource) => void;
  onClose: () => void;
}

const SOURCE_TYPES = [
  { type: "tax_refund" as const, title: "Tax refund", desc: "Annual lump sum you get back from the ATO at EOFY" },
  { type: "cash_buffer" as const, title: "Cash buffer", desc: "Existing savings or offset balance being drawn down over time" },
  { type: "contribution" as const, title: "Contribution", desc: "Recurring contribution from partner, bonus, or other source" },
  { type: "other" as const, title: "Other", desc: "Custom funding source" },
] as const;

export default function AddFundingSourceModal({ onAdd, onClose }: AddFundingSourceModalProps) {
  const [step, setStep] = useState<"type" | "form">("type");
  const [selectedType, setSelectedType] = useState<FundingSource["type"] | null>(null);

  // Form fields
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [frequency, setFrequency] = useState("M");
  const [balance, setBalance] = useState<number>(0);
  const [drawdown, setDrawdown] = useState<number>(0);
  const [notes, setNotes] = useState("");

  function computeMonthly(): number {
    if (!selectedType) return 0;
    if (selectedType === "tax_refund") return amount / 12;
    if (selectedType === "cash_buffer") return drawdown;
    if (selectedType === "contribution") return budgetAmountToMonthly(amount, frequency);
    return amount; // other
  }

  function handleAdd() {
    if (!selectedType || !label.trim()) return;
    const monthly = computeMonthly();
    const now = new Date().toISOString();
    const source: FundingSource = {
      id: crypto.randomUUID(),
      type: selectedType,
      label: label.trim(),
      monthlyAmount: monthly,
      annualAmount: selectedType === "tax_refund" ? amount : undefined,
      openingBalance: selectedType === "cash_buffer" ? balance : undefined,
      drawdownMonthly: selectedType === "cash_buffer" ? drawdown : undefined,
      frequency: selectedType === "contribution" ? frequency : undefined,
      notes: notes.trim() || undefined,
      createdAt: now,
      updatedAt: now,
    };
    onAdd(source);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-2">
          <h3 className="text-lg font-semibold text-neutral-900">
            {step === "type" ? "Add funding source" : `New ${SOURCE_TYPES.find((t) => t.type === selectedType)?.title ?? "source"}`}
          </h3>
        </div>

        <div className="px-6 py-4">
          {step === "type" && (
            <div className="space-y-2">
              {SOURCE_TYPES.map((t) => (
                <button
                  key={t.type}
                  onClick={() => { setSelectedType(t.type); setStep("form"); }}
                  className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
                >
                  <div className="text-sm font-medium text-neutral-900">{t.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{t.desc}</div>
                </button>
              ))}
            </div>
          )}

          {step === "form" && selectedType === "tax_refund" && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Label</label>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. 2025 tax refund" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Annual amount ($)</label>
                <NumberInput value={amount || ""} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} placeholder="e.g. 6000" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              {amount > 0 && <p className="text-xs text-neutral-500">Monthly: {currency(Math.round(amount / 12))}/mo averaged across 12 months</p>}
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Notes (optional)</label>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context..." className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
            </div>
          )}

          {step === "form" && selectedType === "cash_buffer" && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Label</label>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Emergency savings" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Current balance ($)</label>
                <NumberInput value={balance || ""} onChange={(e) => setBalance(parseFloat(e.target.value) || 0)} placeholder="e.g. 10000" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Monthly drawdown ($)</label>
                <NumberInput value={drawdown || ""} onChange={(e) => setDrawdown(parseFloat(e.target.value) || 0)} placeholder="e.g. 500" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              {balance > 0 && drawdown > 0 && <p className="text-xs text-neutral-500">Runway: {Math.round(balance / drawdown)} months at current drawdown</p>}
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Notes (optional)</label>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
            </div>
          )}

          {step === "form" && selectedType === "contribution" && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Label</label>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Nicole's contribution" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Amount ($)</label>
                  <NumberInput value={amount || ""} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} placeholder="e.g. 500" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Frequency</label>
                  <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300">
                    <option value="W">Weekly</option>
                    <option value="F">Fortnightly</option>
                    <option value="M">Monthly</option>
                    <option value="Q">Quarterly</option>
                    <option value="Y">Annually</option>
                  </select>
                </div>
              </div>
              {amount > 0 && <p className="text-xs text-neutral-500">Monthly: {currency(Math.round(budgetAmountToMonthly(amount, frequency)))}/mo</p>}
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Notes (optional)</label>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
            </div>
          )}

          {step === "form" && selectedType === "other" && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Label</label>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Side income" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Monthly amount ($)</label>
                <NumberInput value={amount || ""} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} placeholder="e.g. 200" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Notes (optional)</label>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300" />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-neutral-100">
          <button onClick={step === "form" ? () => setStep("type") : onClose} className="text-sm text-neutral-500 hover:text-neutral-700">
            {step === "form" ? "← Back" : "Cancel"}
          </button>
          {step === "form" && (
            <button
              onClick={handleAdd}
              disabled={!label.trim() || computeMonthly() <= 0}
              className="rounded-xl bg-neutral-900 px-5 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Add funding source
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
